<template>
  <div class="cms-image-upload" :class="{ 'cms-form-group-full': fullWidth, 'cms-image-upload--icon-preview': previewSize === 'icon' }">
    <label v-if="label" class="cms-image-upload-label">{{ label }}</label>
    <p v-if="helpText" class="cms-form-help">{{ helpText }}</p>
    
    <div v-if="modelValue" class="cms-image-preview" :class="{ 'cms-image-preview--icon': previewSize === 'icon' }">
      <img :src="modelValue" :alt="label" />
      <button type="button" class="cms-btn-remove" @click="removeImage">
        <v-icon icon="trash" size="sm" />
      </button>
    </div>
    
    <div class="cms-image-upload-options" :class="{ 'has-image': modelValue }">
      <div v-if="!modelValue" class="cms-image-dropzone">
          <v-uploader
            ref="uploader"
            :initial-value="[]"
            :multiple="false"
            :max-items="1"
            :route-prefix="routePrefix"
            :enable-library="false"
            :after-upload="handleUpload"
            @removed="handleRemove"
          />
      </div>
      <div v-if="!modelValue" class="cms-image-upload-divider">
        <span>{{ $t('cms.media.or') }}</span>
      </div>
      <button type="button" class="cms-btn cms-btn-library" @click="openMediaPicker">
        <v-icon icon="picture" size="md" />
        {{ $t('cms.media.chooseFromLibrary') }}
      </button>
    </div>

    <!-- Media Picker -->
    <cms-media-picker
      v-model="showMediaPicker"
      filter-type="image"
      @selected="handleMediaSelected"
    />
  </div>
</template>

<script lang="ts" setup>
import { ref, watch, onMounted } from 'vue';
import VUploader from '@/components/vendor/upload/VUploader.vue';
import VIcon from '@/components/vendor/basic/icon/VIcon.vue';
import CmsMediaPicker from '@/components/cms/CmsMediaPicker.vue';
import { extractMediaUrl } from '@/helpers/cmsMediaUpload';

interface Props {
  modelValue?: string;
  label?: string;
  helpText?: string;
  fullWidth?: boolean;
  previewSize?: 'default' | 'icon';
  routePrefix?: string;
  initialMedia?: any[];
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  fullWidth: false,
  previewSize: 'default',
  routePrefix: 'api/s3',
  initialMedia: () => [],
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
  (e: 'uploaded', media: any[]): void;
  (e: 'removed', media: any[]): void;
}>();

const uploader = ref<any>(null);
const mediaItems = ref<any[]>([]);
const pendingInitialMedia = ref<any[]>([]);
const showMediaPicker = ref(false);

const updateMediaState = () => {
  if (uploader.value?.mediaLibrary) {
    mediaItems.value = [...uploader.value.mediaLibrary.state.media];
  }
};

const handleUpload = () => {
  updateMediaState();
  const latest = mediaItems.value[mediaItems.value.length - 1];
  const imageUrl = extractMediaUrl(latest);
  if (imageUrl) {
    emit('update:modelValue', imageUrl);
  }
  emit('uploaded', [...mediaItems.value]);
};

const handleRemove = () => {
  emit('update:modelValue', '');
  mediaItems.value = [];
  emit('removed', [...mediaItems.value]);
};

const removeImage = () => {
  emit('update:modelValue', '');
  mediaItems.value = [];
  if (uploader.value?.mediaLibrary) {
    uploader.value.mediaLibrary.state.media = [];
  }
  emit('removed', [...mediaItems.value]);
};

const clearMedia = () => {
  mediaItems.value = [];
  if (uploader.value?.mediaLibrary) {
    uploader.value.mediaLibrary.state.media = [];
  }
};

const setMediaItems = (items: any[] = [], { force = false }: { force?: boolean } = {}) => {
  const normalized = Array.isArray(items) ? items.map((item: any) => JSON.parse(JSON.stringify(item))) : [];
  mediaItems.value = normalized;

  if (uploader.value?.mediaLibrary) {
    uploader.value.mediaLibrary.state.media = normalized.map((item: any) => ({ ...item }));
  } else if (!force) {
    pendingInitialMedia.value = normalized;
  }
};

watch(
  () => props.initialMedia,
  (newItems) => {
    if (newItems && newItems.length > 0) {
      setMediaItems(newItems);
    } else {
      clearMedia();
    }
  },
  { immediate: true, deep: true },
);

onMounted(() => {
  if (pendingInitialMedia.value.length > 0) {
    setMediaItems(pendingInitialMedia.value, { force: true });
    pendingInitialMedia.value = [];
  } else if (props.initialMedia?.length) {
    setMediaItems(props.initialMedia, { force: true });
  } else {
    updateMediaState();
  }
});

defineExpose({
  getMedia: () => mediaItems.value,
  clearMedia,
  setMedia: (items: any[] = []) => setMediaItems(items, { force: true }),
});

watch(() => props.modelValue, (newValue) => {
  if (!newValue) {
    clearMedia();
  }
});

const openMediaPicker = () => {
  showMediaPicker.value = true;
};

const handleMediaSelected = (media: any) => {
  const imageUrl = media.originalUrl || media.original_url || media.url || '';
  if (imageUrl) {
    mediaItems.value = [media];
    emit('update:modelValue', imageUrl);
    emit('uploaded', [media]);
  }
};
</script>

<style lang="scss" scoped>
@import './cms-styles.scss';

.cms-image-upload {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  
  &.cms-form-group-full {
    grid-column: 1 / -1;
  }
}

.cms-image-upload-label {
  font-size: 0.9rem;
  font-weight: 500;
  color: $cms-text-primary;
}

.cms-image-preview {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid $cms-border-color;
  
  img {
    width: 100%;
    height: auto;
    display: block;
  }
  
  &--icon {
    width: 100px;
    height: 100px;
    flex-shrink: 0;
    
    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
    
    .cms-btn-remove {
      top: 0.25rem;
      right: 0.25rem;
      padding: 0.25rem;
    }
  }
  
  .cms-btn-remove {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    background: rgba(255, 0, 0, 0.8);
    color: white;
    border: none;
    border-radius: 6px;
    padding: 0.5rem;
    cursor: pointer;
    transition: background 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
      background: rgba(255, 0, 0, 1);
    }
  }
}

.cms-image-upload-options {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  
  &.has-image {
    margin-top: 1rem;
  }
}

.cms-image-dropzone {
  border: 2px dashed $cms-border-color;
  border-radius: 8px;
  padding: 1rem;
  min-height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cms-image-upload-divider {
  display: flex;
  align-items: center;
  text-align: center;
  color: $cms-text-secondary;
  font-size: 0.9rem;
  
  &::before,
  &::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid $cms-border-color;
  }
  
  span {
    padding: 0 1rem;
  }
}

.cms-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &-secondary {
    background: $cms-background-overlay-hover;
    color: white;
    
    &:hover {
      background: $cms-background-overlay-active;
    }
  }
  
  &-library {
    width: 100%;
    justify-content: center;
    background: $cms-primary-color;
    color: white;
    padding: 1rem 1.5rem;
    font-size: 1rem;
    box-shadow: $cms-shadow-sm;
    
    i, svg, .icon {
      color: white;
    }

    &:hover {
      background: lighten($cms-primary-color, 10%);
      box-shadow: $cms-shadow-md;
      transform: translateY(-2px);
    }
    
    &:active {
      transform: translateY(0);
      box-shadow: $cms-shadow-sm;
    }
    
    :deep(.v-icon) {
      font-size: 1.25rem;
    }
  }
}

.media-library-items, .media-library {
  width: 200px !important;
}
</style>

